// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Categories  (the directory view of pages/Categories.tsx)
// The category directory: delivery strip, a search box that hands off to the menu search, a large featured tile
// (whole fish & steaks) and a grid of category tiles. Each tile shows a real credited cover photo, "N varieties", the
// species as links to their own pages and the cuts the group is offered in; the tile title opens that group's list
// (`?c=<slug>`). Then a "Custom cut transparency" strip built from data/cuts.ts. Everything comes from data/categoryGroups.ts.
// Deliberately NOT here (nothing supports it): "Fresh Landings: 46 varieties" ticker, Popular/Live Stock/Air-Flown badges,
// salmon/exotic, freshwater river, ready-to-cook/masterclass video, dry fish, combos & "save up to 22%", zero ice glaze,
// 0–4 °C chilled promise, the bottom tab bar.
// ─────────────────────────────────────────────────────────────────────────────
import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Scissors, Search } from "lucide-react";
import DeliveryBar from "../menu/DeliveryBar";
import { CATEGORY_GROUPS, type CategoryGroup } from "../../data/categoryGroups";
import { cutsFor, prepLabel } from "../../data/cuts";
import type { SeafoodItem } from "../../data/seafood";
import { getWikimediaSrcSet } from "../../utils/wikimediaImage";

const shortName = (item: SeafoodItem) => item.name.split(" (")[0];

const cutNames = (group: CategoryGroup) => [...new Set(group.items.flatMap((item) => cutsFor(item).map((cut) => cut.name)))];

function Tile({ group, featured }: { group: CategoryGroup; featured: boolean }) {
  const cover = group.items[0];
  const count = group.items.length;
  const cuts = cutNames(group);
  return (
    <li className={`overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10 ${featured ? "sm:col-span-2 lg:col-span-3" : ""}`}>
      <div className={featured ? "sm:flex" : ""}>
        <div className={`relative ${featured ? "sm:w-1/2" : ""}`}>
          <img
            src={cover.image}
            srcSet={getWikimediaSrcSet(cover.image)}
            sizes={featured ? "(min-width: 640px) 50vw, 100vw" : "(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"}
            alt={cover.name}
            title={cover.credit}
            loading="lazy"
            className={`w-full object-cover ${featured ? "h-56 sm:h-full" : "h-40"}`}
          />
          <span className="absolute left-3 top-3 rounded-full bg-ocean-800 px-2.5 py-0.5 text-xs font-bold text-white">
            {count} {count === 1 ? "variety" : "varieties"}
          </span>
        </div>
        <div className="flex flex-1 flex-col p-4">
          <h3 className="font-display text-xl font-bold text-ocean-950 dark:text-foam-50">
            <Link
              to={`/categories?c=${group.slug}`}
              className="inline-flex items-center gap-1.5 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
            >
              {group.title}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </h3>
          <p className="mt-1 text-sm text-ocean-800/85 dark:text-foam-100/80">{group.blurb}</p>
          <p className="mt-2 flex items-start gap-1.5 text-xs text-ocean-800/80 dark:text-foam-100/75">
            <Scissors className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span><span className="sr-only">Cuts offered: </span>{cuts.join(" · ")}</span>
          </p>
          <ul aria-label={`${group.title} species`} className="mt-3 flex flex-wrap gap-2">
            {group.items.map((item) => (
              <li key={item.id}>
                <Link
                  to={`/menu/${item.id}`}
                  className="inline-flex min-h-9 items-center rounded-full bg-ocean-700/5 px-3 text-xs font-semibold text-ocean-900 ring-1 ring-ocean-900/10 hover:bg-ocean-700/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 dark:bg-white/10 dark:text-foam-100 dark:ring-white/10"
                >
                  {shortName(item)}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </li>
  );
}

export default function CategoryDirectory() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const tiles = CATEGORY_GROUPS.filter((group) => group.tile);
  const [featured, ...rest] = tiles;
  const paid = [...new Map(tiles.flatMap((group) => group.items).flatMap((item) => cutsFor(item)).filter((cut) => cut.prepFeePerKg > 0).map((cut) => [cut.name, cut])).values()];

  const search = (e: FormEvent) => {
    e.preventDefault();
    const q = query.trim();
    navigate(q ? `/menu?q=${encodeURIComponent(q)}` : "/menu");
  };

  return (
    <>
      <DeliveryBar />

      <form role="search" onSubmit={search} className="relative">
        <label htmlFor="cat-search" className="sr-only">Search the catch</label>
        <Search className="pointer-events-none absolute left-4 top-3.5 h-5 w-5 text-ocean-800/70 dark:text-foam-100/70" aria-hidden="true" />
        <input
          id="cat-search"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search fish, prawns, crabs, cuts…"
          autoComplete="off"
          className="h-12 w-full rounded-2xl bg-white pl-12 pr-4 text-base text-ocean-950 shadow-sm outline-none ring-1 ring-ocean-900/10 placeholder:text-ocean-800/50 focus:ring-2 focus:ring-teal-600 dark:bg-ocean-900 dark:text-foam-50 dark:ring-white/10"
        />
      </form>

      <ul aria-label="Category directory" className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Tile group={featured} featured />
        {rest.map((group) => (
          <Tile key={group.slug} group={group} featured={false} />
        ))}
      </ul>

      <section aria-labelledby="cut-transparency" className="rounded-2xl bg-ocean-800 p-5 text-white">
        <h2 id="cut-transparency" className="flex items-center gap-2 font-display text-lg font-bold">
          <Scissors className="h-5 w-5" aria-hidden="true" /> Custom cut transparency
        </h2>
        <p className="mt-1 text-sm text-foam-100/90">
          Pick the cut for each catch when you add it. Most cuts are free
          {paid.length > 0 && <>; {paid.map((cut) => `${cut.name} is ${prepLabel(cut)}`).join(", ")}</>}. The price is shown before you add.
        </p>
      </section>
    </>
  );
}
